import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getNotificationStatus, noShowNotificationForAWeek } from '@apis/notification';
import { INotification } from '@dataTypes/notificationTypes';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { NOTIFICATION } from '@components/notification/queryKey/queryKey';

const normalizeNotification = (raw: unknown): INotification | null => {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  if (data.status === 204) return null;
  const popupId = Number(data.popupId);
  if (!popupId) return null;
  const activeValue = data.active ?? data.isActive;
  const imageUrl = data.imageUrl ?? data.image_url;
  const linkUrl = data.linkUrl ?? data.link_url;
  return {
    popupId,
    title: String(data.title ?? ''),
    content: String(data.content ?? ''),
    active: activeValue !== false,
    imageUrl: imageUrl != null && String(imageUrl).trim() !== '' ? String(imageUrl) : null,
    linkUrl: linkUrl != null && String(linkUrl).trim() !== '' ? String(linkUrl) : null,
  };
};

export const useGetNotification = () => {
  const queryClient = useQueryClient();
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [hideModalForAWeek, setHideModalForAWeek] = useState(false);
  const hiddenPopupId = useRef<number | null>(null);

  const {
    data: notificationData,
    isLoading: notificationLoading,
    isFetching,
  } = useQuery<INotification | null>({
    queryKey: [NOTIFICATION.LATESTNOTIFICATION],
    queryFn: async () => {
      try {
        const res = await getNotificationStatus();
        const normalized = normalizeNotification(res);
        if (normalized && normalized.popupId === hiddenPopupId.current) return null;
        return normalized;
      } catch (e: any) {
        if (e?.response?.status === 204) return null;
        return null;
      }
    },
    retry: 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { mutateAsync: noShowNotificationForAWeekAsync } = useMutation({
    mutationFn: (popupId: number) => noShowNotificationForAWeek(popupId),
  });

  useEffect(() => {
    if (notificationLoading || isFetching) return;
    if (
      !notificationData ||
      notificationData.active === false ||
      notificationData.popupId === hiddenPopupId.current
    ) {
      setShowNotificationModal(false);
      return;
    }
    setShowNotificationModal(true);
  }, [notificationData, notificationLoading, isFetching]);

  const closeAsRead = async () => {
    const popupId = notificationData?.popupId;
    if (popupId) hiddenPopupId.current = popupId;
    setShowNotificationModal(false);
    try {
      if (popupId) {
        await noShowNotificationForAWeekAsync(popupId);
      }
    } catch {
      // 이미 읽음인 경우에도 모달은 닫힌 채로 둔다.
    } finally {
      queryClient.setQueryData([NOTIFICATION.LATESTNOTIFICATION], null);
      setHideModalForAWeek(false);
    }
  };

  const toggleHideModalForAWeekHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setHideModalForAWeek(e.target.checked);
  };

  return {
    notificationData,
    showNotificationModal,
    separateModalHandler: closeAsRead,
    hideModalForAWeek,
    toggleHideModalForAWeekHandler,
  };
};
